import { Injectable } from '@angular/core';
import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import _ from 'lodash';
import {
   IDynamicActionsBrickAction,
   IDynamicActionsBrickConfiguration,
   IDynamicActionsBrickEntry,
} from 'src/app/components/bricks/dynamic-actions-brick/dynamic-actions-brick-configuration/dynamic-actions-brick-configuration';
import { UserActionTyp } from 'src/app/samwebDescription/samweb/userActions/userActions';
import {
   ExecuteDynamicActionsBrickUserAction,
   ExecuteDynamicActionsBrickUserActions,
   UpdateDynamicActionsBrickConfiguration,
} from './dynamic-actions-behavior.actions';

export interface IDynamicActionsBehaviorStateModel {
   [brickInstanceId: string]: IDynamicActionsBrickConfiguration;
}

@State<IDynamicActionsBehaviorStateModel>({
   name: 'dynamicActionsBehavior',
   defaults: {},
})
@Injectable()
export class DynamicActionsBehaviorState {
   constructor(private store: Store) {}

   @Selector([DynamicActionsBehaviorState])
   public static getSpecificBehaviorStateOrDefault(state: IDynamicActionsBehaviorStateModel): (brickInstanceId: string) => IDynamicActionsBrickConfiguration {
      return (brickInstanceId: string): IDynamicActionsBrickConfiguration => {
         return state[brickInstanceId] ?? { type: null };
      };
   }

   @Selector([DynamicActionsBehaviorState.getSpecificBehaviorStateOrDefault])
   public static getDynamicActionsBrickConfigurationType(getSpecificBehaviorStateOrDefaultFn: (brickInstanceId: string) => IDynamicActionsBrickConfiguration) {
      return (brickInstanceId: string) => {
         return getSpecificBehaviorStateOrDefaultFn(brickInstanceId)?.type;
      };
   }

   @Selector()
   public static getActionType() {
      return (action: IDynamicActionsBrickAction) => {
         const userActionName = action?.id ?? '';
         let userAction = UserActionTyp.UNKNOWN_ACTION;
         try {
            userAction = UserActionTyp.find(userActionName) ?? UserActionTyp.UNKNOWN_ACTION;
         } catch {
            console.log(`Unknown user action type ${userActionName}`);
         }
         return userAction;
      };
   }

   @Selector()
   public static getActionSetting() {
      return (userAction: UserActionTyp, action: IDynamicActionsBrickAction) => {
         return _.assign(_.cloneDeep(userAction.defaultActionSettings), action.settings);
      };
   }

   @Selector([DynamicActionsBehaviorState.getActionType, DynamicActionsBehaviorState.getActionSetting])
   public static getActionIsToggled(
      getActionType: (action: IDynamicActionsBrickAction) => UserActionTyp,
      getActionSetting: (userAction: UserActionTyp, action: IDynamicActionsBrickAction) => any
   ) {
      return (store: Store, dynamicActionEntry: IDynamicActionsBrickEntry) => {
         return _(dynamicActionEntry.actions)
            .map((action) => {
               const userAction = getActionType(action);
               const settings = getActionSetting(userAction, action);
               return userAction.isActionToggled$(store, settings);
            })
            .min();
      };
   }

   @Selector([DynamicActionsBehaviorState.getActionType, DynamicActionsBehaviorState.getActionSetting])
   public static getActionIsExecutable(
      getActionType: (action: IDynamicActionsBrickAction) => UserActionTyp,
      getActionSetting: (userAction: UserActionTyp, action: IDynamicActionsBrickAction) => any
   ) {
      return (store: Store, dynamicActionEntry: IDynamicActionsBrickEntry) => {
         return _(dynamicActionEntry.actions)
            .map((action) => {
               const userAction = getActionType(action);
               const settings = getActionSetting(userAction, action);
               return userAction.isActionExecutable$(store, settings);
            })
            .min();
      };
   }

   @Action(UpdateDynamicActionsBrickConfiguration)
   @ImmutableContext()
   public updateDynamicActionsJsonConfiguration(ctx: StateContext<IDynamicActionsBehaviorStateModel>, action: UpdateDynamicActionsBrickConfiguration) {
      ctx.setState((state: IDynamicActionsBehaviorStateModel) => {
         try {
            state[action.brickInstanceId] = JSON.parse(action.jsonConfiguration);
         } catch {
            state[action.brickInstanceId] = null;
         }
         return state;
      });
   }

   @Action(ExecuteDynamicActionsBrickUserActions)
   public executeDynamicActionsActions(ctx: StateContext<IDynamicActionsBehaviorStateModel>, action: ExecuteDynamicActionsBrickUserActions) {
      if (_.isNil(action.actions)) {
         return;
      }
      _(action.actions).forEach((a) => this.store.dispatch(new ExecuteDynamicActionsBrickUserAction(a)));
   }

   @Action(ExecuteDynamicActionsBrickUserAction)
   public executeDynamicActionsAction(ctx: StateContext<IDynamicActionsBehaviorStateModel>, action: ExecuteDynamicActionsBrickUserAction) {
      const dynamicAction = action.action;
      if (_.isNil(dynamicAction)) {
         return;
      }
      const userAction = this.store.selectSnapshot(DynamicActionsBehaviorState.getActionType)(dynamicAction);
      const settings = this.store.selectSnapshot(DynamicActionsBehaviorState.getActionSetting)(userAction, dynamicAction);
      return userAction.executeUserAction$(this.store, settings);
   }
}
